import { type Static, Type } from "@fastify/type-provider-typebox";
import { BuyCostType } from "./listing";


// https://developer-docs.amazon.com/sp-api/docs/listings-restrictions-api-v2021-08-01-reference#conditiontype
export enum ProductCondition {
  New = "new_new",
  NewOpenBox = 'new_open_box',
  NewOEM = 'new_oem',

  Refurbished = 'refurbished_refurbished',

  UsedLikeNew = "used_like_new",
  UsedVeryGood = "used_very_good",
  UsedGood = "used_good",
  UsedAcceptable = "used_acceptable",

  CollectibleLikeNew = "collectible_like_new",
  CollectibleVeryGood = 'collectible_very_good',
  CollectibleGood = 'collectible_good',
  CollectibleAcceptable = 'collectible_acceptable',

  Club = 'club_club',
}


export const ProductsQuery = Type.Object({
    page: Type.Optional(Type.Number()),
    pageSize: Type.Optional(Type.Number()),
});
export type ProductsQuery = Static<typeof ProductsQuery>;

//
// List inventory (really, listing items)
//
export const InventoryItem = Type.Object({
    productId: Type.Integer(),
    sku: Type.String(),
    fnSku: Type.String(),
    quantity: Type.Optional(Type.Integer()),
    condition: Type.Optional(Type.String()), // @TODO enum...
    submittedAt: Type.Optional(Type.String()),
    expirationDate: Type.Optional(Type.String()),
    datePurchased: Type.Optional(Type.String()),
    supplierId: Type.Optional(Type.Number()),
    shippingTemplateId: Type.Optional(Type.Number()),

    name: Type.String(),
    asin: Type.String(),
    upc: Type.String(),
    gtin: Type.String(),
    isbn: Type.String(),
    ean: Type.String(),
    image0: Type.String(),
    listPrice: Type.Number(),
    status: Type.String(),
    issues: Type.Unknown(), //Issues are stored on the listings as a strigified JSON payload.
    fulfillmentType: Type.String(),

    averageBuyCost: Type.Optional(Type.String()), // sql numeric type, we parse into a float or null later
    buyCost: Type.Optional(Type.String()),
    buyCostType: Type.Optional(Type.Enum(BuyCostType)),

    strandedDate: Type.Optional(Type.String()),
    strandedRemoveDate: Type.Optional(Type.String()),
    strandedReason: Type.Optional(Type.String()),
    strandedAction: Type.Optional(Type.String())
});

export type InventoryItem = Static<typeof InventoryItem>;

export const ListInventoryResponse = Type.Object({
    total: Type.Integer(),
    data: Type.Array(InventoryItem),
});
export type ListInventoryResponse = Static<typeof ListInventoryResponse>;

export const InventoryError = Type.Object({
    index: Type.Number(),
    instances: Type.Number(),
    fbmInstances: Type.Number(),
    fbaInstances: Type.Number(),
    listings: Type.Array(Type.Object({
        issues: Type.Unknown(),
        asin: Type.String(),
        sku: Type.String(),
        fulfillmentType: Type.String(),
        name: Type.String(),
        condition: Type.String(),
    })),
    error: Type.Object({
        code: Type.String(),
        message: Type.String(),
        severity: Type.String(),
        categories: Type.Array(Type.String()),
        enforcements: Type.Optional(Type.Object({
            actions: Type.Optional(Type.Array(Type.String())),
            exemption: Type.Optional(Type.Object({
                actions: Type.Optional(Type.Array(Type.Object({
                    action: Type.String(),
                }))),
                status: Type.Optional(Type.String()),
            }))
        }))
    }),
});
export type InventoryError = Static<typeof InventoryError>;

export const InventoryErrorsResponse = Type.Object({
    data: Type.Array(InventoryError)
});
export type InventoryErrorsResponse = Static<typeof InventoryErrorsResponse>;

export const PartialInventoryResponse = Type.Array(Type.Object({
    issues: Type.Unknown(),
    asin: Type.String(),
    sku: Type.String(),
    name: Type.String(),
    fulfillmentType: Type.String(),
    condition: Type.String(),
}));
export type PartialInventoryResponse = Static<typeof PartialInventoryResponse>;

export const ProductHazmat = Type.Object({
  aspect: Type.Optional(Type.String()),
  value: Type.Optional(Type.String()),
  marketplace_id: Type.Optional(Type.String()),

  // example Hazmat array (for asin: B00PC5TFEK)
  // [
  //   {
  //     "aspect": "proper_shipping_name",
  //     "value": "RADIOACTIVE MATERIAL, EXCEPTED ARTICLES",
  //     "marketplace_id": "ATVPDKIKX0DER"
  //   },
  //   {
  //     "aspect": "united_nations_regulatory_id",
  //     "value": "UN2911",
  //     "marketplace_id": "ATVPDKIKX0DER"
  //   },
  //   {
  //     "aspect": "transportation_regulatory_class",
  //     "value": "7",
  //     "marketplace_id": "ATVPDKIKX0DER"
  //   },
  //   {
  //     "aspect": "exception",
  //     "value": "US_NoAir",
  //     "marketplace_id": "ATVPDKIKX0DER"
  //   }
  // ]
});
export type ProductHazmat = Static<typeof ProductHazmat>;

export const ProductSearchItem = Type.Object({
    asin: Type.Optional(Type.String()),
    title: Type.Optional(Type.String()),
    image: Type.Optional(Type.String()),
    category: Type.Optional(Type.String()),
    categoryLink: Type.Optional(Type.String()),
    salesRank: Type.Optional(Type.Number()),
    hazmat: Type.Optional(Type.Array(ProductHazmat)),
});
export type ProductSearchItem = Static<typeof ProductSearchItem>;

export const ProductSearchPage = Type.Object({
    total: Type.Number(),
    nextToken: Type.Optional(Type.String()),
    previousToken: Type.Optional(Type.String()),
    pageSize: Type.Number(),
    products: Type.Array(ProductSearchItem),
});
export type ProductSearchPage = Static<typeof ProductSearchPage>;

export enum OfferItemCondition {
    NEW = 'New',
    USED = 'Used',
    COLLECTIBLE = 'Collectible',
    REFURBISHED = 'Refurbished',
    CLUB = 'Club',
}

export enum OfferCustomerType {
    CONSUMER = 'Consumer',
    BUSINESS = 'Business',
}

export const ProductOffersBody = Type.Object({
    marketplaceId: Type.Optional(Type.String()),
    asin: Type.String(),
    condition: Type.Enum(OfferItemCondition),
    offerType: Type.Optional(Type.Enum(OfferCustomerType)),
});
export type ProductOffersBody = Static<typeof ProductOffersBody>;

export const ProductSalesRanksBody = Type.Object({
    asins: Type.Array(Type.String()),
});
export type ProductSalesRanksBody = Static<typeof ProductSalesRanksBody>;

export const ProductSalesRankItem = Type.Object({
    asin: Type.String(),
    salesRank: Type.Optional(Type.Number()),
    category: Type.Optional(Type.String()),
    link: Type.Optional(Type.String()),
    hazmat: Type.Optional(Type.Array(ProductHazmat)),
});
export type ProductSalesRankItem = Static<typeof ProductSalesRankItem>;

export const ProductSalesRanksResponse = Type.Array(ProductSalesRankItem);
export type ProductSalesRanksResponse = Static<typeof ProductSalesRanksResponse>;

export const ProductSyncBody = Type.Object({
    force: Type.Optional(Type.Boolean()),
});
export type ProductSyncBody = Static<typeof ProductSyncBody>;

// @TODO deprecated in v2:
export enum PrepInstruction {
    Polybagging = "Polybagging",
    BubbleWrapping = "BubbleWrapping",
    Taping = "Taping",
    BlackShrinkWrapping = "BlackShrinkWrapping",
    Labeling = "Labeling",
    HangGarment = "HangGarment",
    SetCreation = "SetCreation",
    Boxing = "Boxing",
    RemoveFromHanger = "RemoveFromHanger",
    Debundle = "Debundle",
    SuffocationStickering = "SuffocationStickering",
    CapSealing = "CapSealing",
    SetStickering = "SetStickering",
    BlankStickering = "BlankStickering",
    ShipsInProductPackaging = "ShipsInProductPackaging",
    NoPrep = "NoPrep",
    NONE = "NONE",
}

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#preptype
// @TODO rename 'PrepType'
export const PrepInstructionV2 = Type.Union([
    Type.Literal("ITEM_LABELING"),
    Type.Literal("ITEM_BUBBLEWRAP"),
    Type.Literal("ITEM_POLYBAGGING"),
    Type.Literal("ITEM_TAPING"),
    Type.Literal("ITEM_BLACK_SHRINKWRAP"),
    Type.Literal("ITEM_HANG_GARMENT"),
    Type.Literal("ITEM_BOXING"),
    Type.Literal("ITEM_SETCREAT"),
    Type.Literal("ITEM_RMOVHANG"),
    Type.Literal("ITEM_SUFFOSTK"),
    Type.Literal("ITEM_CAP_SEALING"),
    Type.Literal("ITEM_DEBUNDLE"),
    Type.Literal("ITEM_SETSTK"),
    Type.Literal("ITEM_SIOC"),
    Type.Literal("ITEM_NO_PREP"),
  
    // Type.Literal("ADULT"),
    // Type.Literal("BABY"),
    // Type.Literal("TEXTILE"),
    // Type.Literal("HANGER"),
    // Type.Literal("FRAGILE"),
    // Type.Literal("LIQUID"),
    // Type.Literal("SHARP"),
    // Type.Literal("SMALL"),
    // Type.Literal("PERFORATED"),
    // Type.Literal("GRANULAR"),
    // Type.Literal("SET"),
    // Type.Literal("FC_PROVIDED"),
    // Type.Literal("UNKNOWN"),
    // Type.Literal("NONE"),
]);
export type PrepInstructionV2 = Static<typeof PrepInstructionV2>;

export interface ASINPrepInstructions {
  ASIN?: string;
  BarcodeInstruction?: "RequiresFnskuLabel"
    | "CanUseOriginalBarcode"
    | "MustProviderSellerSKU";
  PrepGuidance?: "ConsultHelpDocuments"
    | "NoAdditionalPrepRequired"
    | "SeePrepInstructionsList";
  PrepInstructionList?: PrepInstruction[];
}
